import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: '[appArsAmountFormatter]',
})
export class ArsAmountFormatterDirective {
  /**
   * Emite el valor numérico listo para enviar al backend (con punto decimal).
   * Ej.: "2.500,50" -> 2500.5
   */
  @Output() numericValueChange = new EventEmitter<number>();

  private lastNumericValue = 0;

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  // Permitir sólo números y una coma/punto mientras escribe. No formateamos aún a 2 decimales.
  @HostListener('input', ['$event.target.value'])
    onInput(value: string) {
        // Si está vacío, no tocar el input ni emitir 0
        if (!value || value.trim() === '') {
        this.renderer.setProperty(this.el.nativeElement, 'value', '');
        this.numericValueChange.emit(undefined);
        return;
        }
    
        const cleaned = this.sanitizeForTyping(value);
        this.renderer.setProperty(this.el.nativeElement, 'value', cleaned);
    
        const numeric = this.parseToNumber(cleaned);
        // Si no es número válido, no forzar 0
        if (!isFinite(numeric)) {
        this.numericValueChange.emit(undefined);
        return;
        }
        this.numericValueChange.emit(numeric);
    }

  // Al pegar, limpiamos de inmediato y aplicamos la misma lógica que input
  @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent) {
      event.preventDefault();
      const pasted = event.clipboardData?.getData('text/plain') ?? '';
      if (!pasted || pasted.trim() === '') {
        this.renderer.setProperty(this.el.nativeElement, 'value', '');
        this.numericValueChange.emit(undefined);
        return;
      }
      const cleaned = this.sanitizeForTyping(pasted);
      this.renderer.setProperty(this.el.nativeElement, 'value', cleaned);
    
      const numeric = this.parseToNumber(cleaned);
      this.numericValueChange.emit(isFinite(numeric) ? numeric : undefined);
    }

  // Al tomar foco, mostramos sin formateo estricto para facilitar edición (coma como separador decimal).
  @HostListener('focus')
    onFocus() {
      // no hacemos nada; dejamos tal cual para que el usuario edite
    }

  // Al perder foco, formateamos a 2 decimales con coma (sin separador de miles para respetar tu ejemplo).
  @HostListener('blur')
    onBlur() {
      const current = this.el.nativeElement.value;
      if (!current || current.trim() === '') {
        // dejar vacío, no “0,00”
        this.renderer.setProperty(this.el.nativeElement, 'value', '');
        this.numericValueChange.emit(undefined);
        return;
      }
    
      const numeric = this.parseToNumber(current);
      if (!isFinite(numeric)) {
        // valor inválido -> lo dejamos vacío
        this.renderer.setProperty(this.el.nativeElement, 'value', '');
        this.numericValueChange.emit(undefined);
        return;
      }
    
      const formatted = this.formatEsArTwoDecimals(numeric); // ej: "2500,00"
      this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
      this.numericValueChange.emit(numeric); // para backend: 2500 o 2500.5
    }

  // ===== Helpers =====

  // Mantener sólo dígitos y una coma o punto (primera ocurrencia). Quitar símbolos, espacios, etc.
  private sanitizeForTyping(input: string): string {
    // Quitar todo menos dígitos, coma, punto
    let out = input.replace(/[^\d.,]/g, '');

    // Normalizar: permitir solo una coma o punto; si hay más, las extra quitarlas
    const firstComma = out.indexOf(',');
    const firstDot = out.indexOf('.');
    let firstSep = -1;

    if (firstComma === -1 && firstDot === -1) {
      firstSep = -1;
    } else if (firstComma === -1) {
      firstSep = firstDot;
    } else if (firstDot === -1) {
      firstSep = firstComma;
    } else {
      firstSep = Math.min(firstComma, firstDot);
    }

    if (firstSep >= 0) {
      const head = out.slice(0, firstSep + 1);
      const tail = out
        .slice(firstSep + 1)
        .replace(/[.,]/g, ''); // quitar separadores extra
      out = head + tail;
    }

    return out;
  }

  // Convertir cualquier string (con coma o punto) a número JS (punto decimal).
private parseToNumber(input: string): number {
  if (!input) return NaN;
  const normalized = input.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(normalized);
  return isNaN(n) ? NaN : n;
}

  // Mostrar para edición: si tiene parte decimal, usar coma; si no, sólo enteros.
  private unformatForEditing(value: number): string {
    if (!isFinite(value)) return '';
    const hasDecimals = Math.floor(value) !== value;
    return hasDecimals ? String(value).replace('.', ',') : String(Math.trunc(value));
  }

  // Formato vista AR: dos decimales con coma (sin separador de miles según tu ejemplo: "2500,00").
  private formatEsArTwoDecimals(value: number): string {
    if (!isFinite(value)) return '0,00';
    const fixed = value.toFixed(2); // "2500.50"
    return fixed.replace('.', ','); // "2500,50"
  }
}
